import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import "katex/dist/katex.min.css";
import "katex/dist/contrib/mhchem.min.js";
import DOMPurify from "dompurify";

const SAFE_HTML_OPTS = { ALLOWED_TAGS: ["strong", "em", "br"], ALLOWED_ATTR: ["class"] };
const safe = (html) => DOMPurify.sanitize(html, SAFE_HTML_OPTS);

// Render text with LaTeX formulas
// Supports both inline ($...$) and block ($$...$$) math
// Currency symbols (\$) are preserved
export const MathRenderer = ({ content }) => {
  if (!content) return null;

  // First, replace escaped dollar signs with placeholder
  let processedContent = content.replace(/\\\$/g, '%%CURRENCY%%');

  // Split content by math delimiters
  const parts = [];
  let remaining = processedContent;
  let key = 0;

  // Process block math first ($$...$$)
  while (remaining.length > 0) {
    const blockStart = remaining.indexOf("$$");
    
    if (blockStart === -1) {
      // No more block math, process inline math
      parts.push(...processInlineMath(remaining, key));
      break;
    }

    // Add text before block math
    if (blockStart > 0) {
      parts.push(...processInlineMath(remaining.substring(0, blockStart), key));
      key += 100;
    }

    // Find end of block math
    const blockEnd = remaining.indexOf("$$", blockStart + 2);
    if (blockEnd === -1) {
      // No closing $$, treat as regular text
      parts.push(...processInlineMath(remaining, key));
      break;
    }

    // Extract and render block math
    const mathContent = remaining.substring(blockStart + 2, blockEnd);
    parts.push(
      <div key={key++} className="my-3 overflow-x-auto">
        <ErrorBoundary fallback={<code className="text-red-500">{mathContent}</code>}>
          <BlockMath math={mathContent} />
        </ErrorBoundary>
      </div>
    );

    remaining = remaining.substring(blockEnd + 2);
  }

  return <>{parts}</>;
};

// Process inline math ($...$)
const processInlineMath = (text, startKey) => {
  const parts = [];
  let remaining = text;
  let key = startKey;

  while (remaining.length > 0) {
    // Find single $ (but not $$)
    let mathStart = -1;
    for (let i = 0; i < remaining.length; i++) {
      if (remaining[i] === "$" && remaining[i + 1] !== "$" && (i === 0 || remaining[i - 1] !== "$")) {
        mathStart = i;
        break;
      }
    }

    if (mathStart === -1) {
      // No more inline math - apply text formatting and restore currency symbols
      if (remaining.length > 0) {
        const restoredText = remaining.replace(/%%CURRENCY%%/g, '$');
        const formattedText = restoredText
          .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>');
        parts.push(<span key={key++} dangerouslySetInnerHTML={{ __html: safe(formattedText) }} />);
      }
      break;
    }

    // Add text before inline math with formatting
    if (mathStart > 0) {
      const textBefore = remaining.substring(0, mathStart).replace(/%%CURRENCY%%/g, '$');
      const formattedText = textBefore
        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>');
      parts.push(<span key={key++} dangerouslySetInnerHTML={{ __html: safe(formattedText) }} />);
    }

    // Find end of inline math
    let mathEnd = -1;
    for (let i = mathStart + 1; i < remaining.length; i++) {
      if (remaining[i] === "$" && remaining[i + 1] !== "$") {
        mathEnd = i;
        break;
      }
    }

    if (mathEnd === -1) {
      // No closing $, treat as regular text
      const restoredText = remaining.replace(/%%CURRENCY%%/g, '$');
      const formattedText = restoredText
        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>');
      parts.push(<span key={key++} dangerouslySetInnerHTML={{ __html: safe(formattedText) }} />);
      break;
    }

    // Extract and render inline math
    const mathContent = remaining.substring(mathStart + 1, mathEnd);
    parts.push(
      <span key={key++} className="mx-0.5">
        <ErrorBoundary fallback={<code className="text-red-500 text-sm">{mathContent}</code>}>
          <InlineMath math={mathContent} />
        </ErrorBoundary>
      </span>
    );

    remaining = remaining.substring(mathEnd + 1);
  }

  return parts;
};

// Error boundary for KaTeX
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.children !== this.props.children) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

export default MathRenderer;
